import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useLoan } from "../hooks/useLoan";
import {
  LOAN_STATUS, SLOT_STATUS_META, slotStatus, slotLabel, slotImage,
  getSlotNumber, pad2, formatCountdown,
} from "../lib/loans";

// Page utilisée pour toute catégorie créée depuis l'admin
// qui n'a pas (encore) de page dédiée. Route : /equipment/:catId

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff",
    green: "#16a34a", greenBg: "#f0fdf4",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff", shadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f",
    green: "#4ade80", greenBg: "#052e16",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b", shadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

function Shell({ t, dark, toggleTheme, children }) {
  const navigate = useNavigate();
  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/equipment")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function SlotGrid({ slots, mineDocId, emoji, t }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${slots.length <= 4 ? 2 : 3}, 1fr)`, gap: 8, marginBottom: 16 }}>
      {slots.map(s => {
        const st = slotStatus(s);
        const mine = s.docId === mineDocId;
        const color = mine ? t.blue : SLOT_STATUS_META[st].color;
        const dim = st !== "available" && !mine;
        const img = slotImage(s);
        return (
          <div key={s.docId} style={{ background: t.cardBg, border: `2px solid ${color}`, borderRadius: 12, padding: "10px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, position: "relative", boxShadow: t.shadow }}>
            <div style={{ position: "absolute", top: 6, left: 8, fontSize: 9, fontWeight: 700, color: t.text3, fontFamily: "monospace" }}>#{pad2(getSlotNumber(s))}</div>
            <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center", opacity: dim ? 0.35 : 1, marginTop: 4 }}>
              {img ? <img src={img} alt="" style={{ maxHeight: 56, maxWidth: "100%", objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                   : <span style={{ fontSize: 28 }}>{emoji}</span>}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.text1, textAlign: "center", lineHeight: 1.2 }}>{s.short || slotLabel(s)}</div>
            <div style={{ fontSize: 9, fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: 20, padding: "1px 7px" }}>
              {mine ? "Le vôtre" : SLOT_STATUS_META[st].label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LoanFlow({ category, t }) {
  const emoji = category.emoji || "📦";
  const title = category.title || "Équipement";
  const accent = (t === theme.dark ? category.darkColor : category.color) || t.blue;

  const {
    user, ready, slots, availableSlots, loan, loanSlot, loanMinutes,
    secondsLeft, overdue, busy, error, clearError, justClosed,
    borrow, reportCondition, declareReturn,
  } = useLoan(category.collection);

  const slotDisplay = pad2(loan?.slotNumber ?? getSlotNumber(loanSlot));
  const loanName = loan?.slotName || slotLabel(loanSlot);
  const n = availableSlots.length;

  const btn = (bg, color = "#fff", extra = {}) => ({
    width: "100%", border: "none", borderRadius: 14, background: bg, color,
    fontSize: 15, fontWeight: 700, padding: 15, cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.7 : 1, ...extra,
  });

  if (!ready) return <div style={{ textAlign: "center", color: t.text3, fontSize: 13 }}>Chargement…</div>;
  if (!user) return <div style={{ textAlign: "center", color: t.text2, fontSize: 14 }}>Connectez-vous pour emprunter.</div>;

  if (justClosed && !loan) return (
    <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
      <div style={{ fontSize: 13, color: t.text2 }}>{justClosed === "returned" ? "Retour validé par l'accueil." : "Votre emprunt a été clôturé."}</div>
    </div>
  );

  if (!loan) return (
    <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
      {slots.length > 0 && <SlotGrid slots={slots} mineDocId={null} emoji={emoji} t={t} />}
      <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>{emoji} {title}</div>
      <div style={{ fontSize: 14, color: t.text2, marginBottom: 20 }}>
        {slots.length === 0 ? "Aucun équipement configuré pour le moment"
          : n > 0 ? `${n} disponible${n > 1 ? "s" : ""}` : "Aucun disponible pour le moment"}
      </div>
      {error && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error === "none" ? "Quelqu'un vient de prendre le dernier." : error}</div>}
      <button disabled={busy || n === 0} onClick={() => { clearError(); borrow(); }}
        style={btn(n > 0 ? accent : t.bg3, n > 0 ? "#fff" : t.text3, { fontSize: 16, padding: 16, cursor: n > 0 ? "pointer" : "not-allowed", opacity: n > 0 ? 1 : 0.6 })}>
        Emprunter
      </button>
      <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>
        {loanMinutes ? `Durée de prêt : ${loanMinutes} minutes • ` : ""}Retour validé par l'accueil
      </div>
    </div>
  );

  if (loan.status === LOAN_STATUS.RETURN_PENDING) return (
    <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
      <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
        <svg width="100" height="100" style={{ animation: "spin 1.6s linear infinite" }}>
          <circle cx="50" cy="50" r="42" fill="none" stroke={t.bg3} strokeWidth="6" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={t.orange} strokeWidth="6" strokeLinecap="round" strokeDasharray="70 194" />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>{emoji}</div>
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Retour en attente de validation</div>
      <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>L'accueil va vérifier <strong style={{ color: t.text1 }}>{loanName}</strong>.<br />Vous pouvez quitter cette page.</div>
    </div>
  );

  if (!loan.condition) return (
    <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
      <SlotGrid slots={slots} mineDocId={loan.slotDocId} emoji={emoji} t={t} />
      <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Prenez l'emplacement</div>
      <div style={{ width: 110, height: 110, borderRadius: "50%", background: t.blueBg, border: `3px solid ${accent}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", animation: "pop 0.4s ease" }}>
        <span style={{ fontSize: 48, fontWeight: 900, color: accent, lineHeight: 1 }}>{slotDisplay}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: t.text1, marginBottom: 20 }}>{loanName}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 12 }}>Il fonctionne bien ?</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button disabled={busy} onClick={() => reportCondition("good")} style={btn(t.green, "#fff", { fontSize: 14, padding: 14 })}>✅ Oui, tout va bien</button>
        <button disabled={busy} onClick={() => reportCondition("defective")} style={btn(t.redBg, t.red, { fontSize: 14, padding: 14, border: `1.5px solid ${t.redBorder}` })}>⚠️ Non, il est défectueux</button>
      </div>
    </div>
  );

  return (
    <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>{emoji}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 4 }}>{loanName}</div>
      <div style={{ fontSize: 13, color: t.text2, marginBottom: 20 }}>Emplacement {slotDisplay}</div>
      {secondsLeft !== null && (
        <div style={{ display: "inline-block", background: overdue ? t.redBg : t.orangeBg, border: `1px solid ${overdue ? t.redBorder : t.orangeBorder}`, borderRadius: 20, padding: "6px 14px", marginBottom: 24, animation: overdue ? "pulse 1s infinite" : "none" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: overdue ? t.red : t.orange, fontVariantNumeric: "tabular-nums" }}>
            {overdue ? "⚠️ Temps écoulé — merci de le rendre" : `⏱ ${formatCountdown(secondsLeft)} restantes`}
          </span>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
      <button disabled={busy} onClick={() => { clearError(); declareReturn(); }} style={btn(overdue ? t.red : accent)}>
        J'ai rendu l'équipement
      </button>
    </div>
  );
}

export default function GenericEquipmentPage() {
  const { catId } = useParams();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [category, setCategory] = useState(undefined); // undefined = chargement, null = introuvable
  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  useEffect(() => onSnapshot(doc(db, "equipment_categories", catId),
    s => setCategory(s.exists() ? { id: s.id, ...s.data() } : null),
    () => setCategory(null)), [catId]);

  let content;
  if (category === undefined) content = <div style={{ textAlign: "center", color: t.text3, fontSize: 13 }}>Chargement…</div>;
  else if (!category || !category.collection || category.active === false) content = (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{category?.emoji || "📦"}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: t.text1, marginBottom: 6 }}>{category?.title || "Catégorie introuvable"}</div>
      <div style={{ fontSize: 13, color: t.text2 }}>Bientôt disponible.</div>
    </div>
  );
  else content = <LoanFlow key={category.collection} category={category} t={t} />;

  return <Shell t={t} dark={dark} toggleTheme={toggleTheme}>{content}</Shell>;
}
